import { z } from 'zod';

// src/brand.ts
var SITE_NAME = "VEO Consultancy";
var SITE_TAGLINE = "Your Vision. Our Expertise.";
var CORE_SERVICE_SLUGS = [
  "offshore-and-local-stock-advisory",
  "retirement-planning",
  "financial-coaching"
];
var SYSTEM_PAGE_PATHS = [
  "/",
  "/services",
  "/about",
  "/contact",
  "/book-consultation",
  "/privacy-policy",
  "/terms-and-conditions",
  "/risk-disclaimer"
];
var ROLE_KEYS = [
  "SUPER_ADMIN",
  "CONTENT_MANAGER",
  "EDITOR",
  "ADVISOR",
  "MARKETING_USER",
  "AUDITOR"
];

// src/permissions.ts
var PERMISSION_KEYS = [
  // --- Dashboard -----------------------------------------------------------
  "dashboard:view",
  // --- Page builder --------------------------------------------------------
  "pages:read",
  "pages:create",
  "pages:update",
  "pages:delete",
  "pages:publish",
  "pages:restore-version",
  "pages:preview-draft",
  "sections:create",
  "sections:update",
  "sections:delete",
  "sections:reorder",
  "blocks:read",
  "blocks:create",
  "blocks:update",
  "blocks:delete",
  // --- Services ------------------------------------------------------------
  "services:read",
  "services:create",
  "services:update",
  "services:delete",
  "services:publish",
  "benefits:read",
  "benefits:manage",
  "process-steps:read",
  "process-steps:manage",
  // --- Insights ------------------------------------------------------------
  "posts:read",
  "posts:read:all",
  "posts:create",
  "posts:update",
  "posts:update:own",
  "posts:delete",
  "posts:delete:own",
  "posts:publish",
  "posts:schedule",
  "categories:read",
  "categories:manage",
  "tags:read",
  "tags:create",
  "tags:manage",
  // --- Supporting content --------------------------------------------------
  "faqs:read",
  "faqs:create",
  "faqs:update",
  "faqs:delete",
  "testimonials:read",
  "testimonials:create",
  "testimonials:update",
  "testimonials:delete",
  "testimonials:publish",
  "team:read",
  "team:create",
  "team:update",
  "team:delete",
  // --- Media ---------------------------------------------------------------
  "media:read",
  "media:upload",
  "media:update",
  "media:delete",
  "media:manage-folders",
  // --- Leads ---------------------------------------------------------------
  "consultations:read",
  "consultations:read:all",
  "consultations:update",
  "consultations:assign",
  "consultations:delete",
  "consultations:export",
  "consultations:note",
  "messages:read",
  "messages:read:all",
  "messages:update",
  "messages:delete",
  "messages:reply",
  "subscribers:read",
  "subscribers:create",
  "subscribers:update",
  "subscribers:delete",
  "subscribers:export",
  // --- Site configuration --------------------------------------------------
  "navigation:read",
  "navigation:create",
  "navigation:update",
  "navigation:delete",
  "seo:read",
  "seo:update",
  "seo:manage-global",
  "redirects:read",
  "redirects:manage",
  "settings:read",
  "settings:update",
  "settings:update:secure",
  // --- Governance ----------------------------------------------------------
  "users:read",
  "users:create",
  "users:update",
  "users:delete",
  "users:invite",
  "users:force-logout",
  "users:assign-roles",
  "roles:read",
  "roles:create",
  "roles:update",
  "roles:delete",
  "roles:assign-permissions",
  "permissions:read",
  "audit:read",
  "audit:export"
];
var PERMISSION_DESCRIPTIONS = {
  "dashboard:view": "View the admin dashboard",
  "pages:read": "View pages and their sections",
  "pages:create": "Create new pages",
  "pages:update": "Edit page content and settings",
  "pages:delete": "Delete pages that are not system pages",
  "pages:publish": "Publish and unpublish pages",
  "pages:restore-version": "Restore a page from version history",
  "pages:preview-draft": "Preview unpublished pages on the public site",
  "sections:create": "Add sections to a page",
  "sections:update": "Edit section content and appearance",
  "sections:delete": "Remove sections from a page",
  "sections:reorder": "Change the order of sections on a page",
  "blocks:read": "View the reusable block library",
  "blocks:create": "Create reusable blocks",
  "blocks:update": "Edit reusable blocks, affecting every page using them",
  "blocks:delete": "Delete reusable blocks",
  "services:read": "View services",
  "services:create": "Create services",
  "services:update": "Edit service details",
  "services:delete": "Delete services",
  "services:publish": "Publish and unpublish services",
  "benefits:read": "View service benefits",
  "benefits:manage": "Create, edit and remove service benefits",
  "process-steps:read": "View process steps",
  "process-steps:manage": "Create, edit and remove process steps",
  "posts:read": "View insight articles",
  "posts:read:all": "View all articles, not only your own",
  "posts:create": "Write new articles",
  "posts:update": "Edit any article",
  "posts:update:own": "Edit only articles you authored",
  "posts:delete": "Delete any article",
  "posts:delete:own": "Delete only your own unpublished drafts",
  "posts:publish": "Publish and unpublish articles",
  "posts:schedule": "Schedule articles for future publication",
  "categories:read": "View article categories",
  "categories:manage": "Create, edit and remove categories",
  "tags:read": "View article tags",
  "tags:create": "Create new tags while writing",
  "tags:manage": "Create, edit and remove tags",
  "faqs:read": "View FAQs",
  "faqs:create": "Create FAQs",
  "faqs:update": "Edit FAQs",
  "faqs:delete": "Delete FAQs",
  "testimonials:read": "View testimonials",
  "testimonials:create": "Add testimonials",
  "testimonials:update": "Edit testimonials",
  "testimonials:delete": "Delete testimonials",
  "testimonials:publish": "Publish testimonials, which requires recorded consent",
  "team:read": "View team members",
  "team:create": "Add team members",
  "team:update": "Edit team member profiles",
  "team:delete": "Remove team members",
  "media:read": "Browse the media library",
  "media:upload": "Upload files",
  "media:update": "Edit file details and alt text",
  "media:delete": "Delete files",
  "media:manage-folders": "Create and reorganise media folders",
  "consultations:read": "View consultation requests assigned to you",
  "consultations:read:all": "View all consultation requests",
  "consultations:update": "Update the status of a consultation request",
  "consultations:assign": "Assign consultation requests to advisors",
  "consultations:delete": "Delete consultation requests",
  "consultations:export": "Export consultation requests to CSV",
  "consultations:note": "Add internal notes to a consultation request",
  "messages:read": "View contact messages assigned to you",
  "messages:read:all": "View all contact messages",
  "messages:update": "Update the status of a contact message",
  "messages:delete": "Delete contact messages",
  "messages:reply": "Mark a contact message as replied",
  "subscribers:read": "View newsletter subscribers",
  "subscribers:create": "Add newsletter subscribers manually",
  "subscribers:update": "Edit subscriber records",
  "subscribers:delete": "Remove subscribers",
  "subscribers:export": "Export the subscriber list to CSV",
  "navigation:read": "View navigation menus",
  "navigation:create": "Add navigation items",
  "navigation:update": "Edit and reorder navigation items",
  "navigation:delete": "Remove navigation items",
  "seo:read": "View SEO metadata",
  "seo:update": "Edit SEO metadata for individual pages and articles",
  "seo:manage-global": "Edit site-wide SEO defaults",
  "redirects:read": "View redirects",
  "redirects:manage": "Create, edit and remove redirects",
  "settings:read": "View site settings",
  "settings:update": "Edit non-sensitive site settings",
  "settings:update:secure": "Edit credentials and other sensitive settings",
  "users:read": "View user accounts",
  "users:create": "Create user accounts",
  "users:update": "Edit user accounts",
  "users:delete": "Deactivate user accounts",
  "users:invite": "Invite new users by email",
  "users:force-logout": "End another user's active sessions",
  "users:assign-roles": "Change which roles a user holds",
  "roles:read": "View roles and their permissions",
  "roles:create": "Create custom roles",
  "roles:update": "Edit role names and descriptions",
  "roles:delete": "Delete custom roles",
  "roles:assign-permissions": "Change the permissions attached to a role",
  "permissions:read": "View the permission catalogue",
  "audit:read": "View the audit log",
  "audit:export": "Export audit log entries"
};
var ROLE_DEFINITIONS = {
  SUPER_ADMIN: {
    key: "SUPER_ADMIN",
    name: "Super Admin",
    description: "Unrestricted access, including user accounts, roles and sensitive settings.",
    sortOrder: 1
  },
  CONTENT_MANAGER: {
    key: "CONTENT_MANAGER",
    name: "Content Manager",
    description: "Owns all site content end to end and can publish. Cannot change user access or credentials.",
    sortOrder: 2
  },
  EDITOR: {
    key: "EDITOR",
    name: "Editor",
    description: "Writes and edits content but cannot publish it. Article permissions are limited to their own work.",
    sortOrder: 3
  },
  ADVISOR: {
    key: "ADVISOR",
    name: "Advisor",
    description: "Works consultation requests and contact messages assigned to them. Read-only on site content.",
    sortOrder: 4
  },
  MARKETING_USER: {
    key: "MARKETING_USER",
    name: "Marketing User",
    description: "Owns insights, SEO, media, testimonials and the subscriber list, including publishing.",
    sortOrder: 5
  },
  AUDITOR: {
    key: "AUDITOR",
    name: "Read-Only Auditor",
    description: "Reads everything including the audit log, and can export. Holds no permission that writes.",
    sortOrder: 6
  }
};
var CONTENT_MANAGER_PERMISSIONS = [
  "dashboard:view",
  "pages:read",
  "pages:create",
  "pages:update",
  "pages:delete",
  "pages:publish",
  "pages:restore-version",
  "pages:preview-draft",
  "sections:create",
  "sections:update",
  "sections:delete",
  "sections:reorder",
  "blocks:read",
  "blocks:create",
  "blocks:update",
  "blocks:delete",
  "services:read",
  "services:create",
  "services:update",
  "services:delete",
  "services:publish",
  "benefits:read",
  "benefits:manage",
  "process-steps:read",
  "process-steps:manage",
  "posts:read",
  "posts:read:all",
  "posts:create",
  "posts:update",
  "posts:delete",
  "posts:publish",
  "posts:schedule",
  "categories:read",
  "categories:manage",
  "tags:read",
  "tags:create",
  "tags:manage",
  "faqs:read",
  "faqs:create",
  "faqs:update",
  "faqs:delete",
  "testimonials:read",
  "testimonials:create",
  "testimonials:update",
  "testimonials:delete",
  "testimonials:publish",
  "team:read",
  "team:create",
  "team:update",
  "team:delete",
  "media:read",
  "media:upload",
  "media:update",
  "media:delete",
  "media:manage-folders",
  "consultations:read",
  "consultations:read:all",
  "messages:read",
  "messages:read:all",
  "subscribers:read",
  "navigation:read",
  "navigation:create",
  "navigation:update",
  "navigation:delete",
  "seo:read",
  "seo:update",
  "seo:manage-global",
  "redirects:read",
  "redirects:manage",
  "settings:read",
  "settings:update",
  "users:read",
  "audit:read"
];
var EDITOR_PERMISSIONS = [
  "dashboard:view",
  "pages:read",
  "pages:update",
  "pages:preview-draft",
  "sections:create",
  "sections:update",
  "sections:delete",
  "sections:reorder",
  "blocks:read",
  "services:read",
  "services:update",
  "benefits:read",
  "benefits:manage",
  "process-steps:read",
  "process-steps:manage",
  // No `posts:read:all`: an Editor sees the list, but ownership scoping in the
  // service layer restricts edits to their own byline.
  "posts:read",
  "posts:create",
  "posts:update:own",
  "posts:delete:own",
  "categories:read",
  "tags:read",
  "tags:create",
  "faqs:read",
  "faqs:create",
  "faqs:update",
  "testimonials:read",
  "testimonials:create",
  "testimonials:update",
  "team:read",
  "team:update",
  "media:read",
  "media:upload",
  "media:update",
  "seo:read",
  "seo:update"
];
var ADVISOR_PERMISSIONS = [
  "dashboard:view",
  "pages:read",
  "services:read",
  "benefits:read",
  "process-steps:read",
  "posts:read",
  "posts:read:all",
  "categories:read",
  "tags:read",
  "faqs:read",
  "testimonials:read",
  "testimonials:create",
  "team:read",
  "media:read",
  // Without `consultations:read:all`, an Advisor sees only what is assigned to
  // them. That scoping is enforced in the service layer, not the guard.
  "consultations:read",
  "consultations:update",
  "consultations:assign",
  "consultations:note",
  "messages:read",
  "messages:update",
  "messages:reply"
];
var MARKETING_PERMISSIONS = [
  "dashboard:view",
  "pages:read",
  "pages:preview-draft",
  "blocks:read",
  "services:read",
  "benefits:read",
  "process-steps:read",
  "posts:read",
  "posts:read:all",
  "posts:create",
  "posts:update",
  "posts:delete",
  "posts:publish",
  "posts:schedule",
  "categories:read",
  "categories:manage",
  "tags:read",
  "tags:create",
  "tags:manage",
  "faqs:read",
  "testimonials:read",
  "testimonials:create",
  "testimonials:update",
  "testimonials:delete",
  "testimonials:publish",
  "team:read",
  "media:read",
  "media:upload",
  "media:update",
  "media:delete",
  "media:manage-folders",
  "consultations:read",
  "consultations:read:all",
  "messages:read",
  "messages:read:all",
  "subscribers:read",
  "subscribers:create",
  "subscribers:update",
  "subscribers:delete",
  "subscribers:export",
  "navigation:read",
  "seo:read",
  "seo:update",
  "seo:manage-global",
  "redirects:read",
  "redirects:manage",
  "settings:read",
  "settings:update"
];
var AUDITOR_PERMISSIONS = PERMISSION_KEYS.filter((key) => {
  const action = key.slice(key.indexOf(":") + 1);
  return action === "read" || action === "read:all" || action === "export" || key === "dashboard:view";
});
var ROLE_PERMISSIONS = {
  SUPER_ADMIN: PERMISSION_KEYS,
  CONTENT_MANAGER: CONTENT_MANAGER_PERMISSIONS,
  EDITOR: EDITOR_PERMISSIONS,
  ADVISOR: ADVISOR_PERMISSIONS,
  MARKETING_USER: MARKETING_PERMISSIONS,
  AUDITOR: AUDITOR_PERMISSIONS
};
function parsePermissionKey(key) {
  const separator = key.indexOf(":");
  return {
    resource: key.slice(0, separator),
    action: key.slice(separator + 1)
  };
}
function hasPermission(granted, required) {
  return granted.includes(required);
}
function hasAllPermissions(granted, required) {
  return required.every((permission) => granted.includes(permission));
}
function hasAnyPermission(granted, required) {
  return required.some((permission) => granted.includes(permission));
}
function resolvePermissions(roles) {
  const resolved = /* @__PURE__ */ new Set();
  for (const role of roles) {
    for (const permission of ROLE_PERMISSIONS[role]) {
      resolved.add(permission);
    }
  }
  return [...resolved];
}
var passwordSchema = z.string().min(12, "Password must be at least 12 characters").max(128, "Password must be at most 128 characters").refine(
  (value) => /[a-z]/.test(value) && (/[A-Z]/.test(value) || /\d/.test(value) || /[^\w\s]/.test(value)),
  "Password must mix letters with at least one capital, number or symbol"
);
var emailSchema = z.email("Enter a valid email address").max(254).transform((value) => value.trim().toLowerCase());
var loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "Enter your password").max(128),
  /** Extends the refresh token lifetime on a trusted device. */
  rememberMe: z.boolean().optional().default(false)
});
var forgotPasswordSchema = z.object({
  email: emailSchema
});
var resetPasswordSchema = z.object({
  token: z.string().min(20, "This reset link is not valid"),
  password: passwordSchema,
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
});
var acceptInviteSchema = z.object({
  token: z.string().min(20, "This invitation link is not valid"),
  password: passwordSchema,
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
});
var changePasswordSchema = z.object({
  currentPassword: z.string().min(1, "Enter your current password"),
  password: passwordSchema,
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
}).refine((data) => data.currentPassword !== data.password, {
  message: "New password must be different from your current password",
  path: ["password"]
});
var updateProfileSchema = z.object({
  firstName: z.string().min(1, "Enter your first name").max(80),
  lastName: z.string().min(1, "Enter your last name").max(80),
  jobTitle: z.string().max(120).optional().nullable()
});
var authUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  jobTitle: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  roles: z.array(z.enum(ROLE_KEYS)),
  permissions: z.array(z.enum(PERMISSION_KEYS)),
  mustChangePassword: z.boolean(),
  lastLoginAt: z.string().nullable()
});
var loginResponseSchema = z.object({
  accessToken: z.string(),
  /** Seconds until the access token expires. */
  expiresIn: z.number(),
  user: authUserSchema
});
var sessionSchema = z.object({
  id: z.string(),
  userAgent: z.string().nullable(),
  ipAddress: z.string().nullable(),
  createdAt: z.string(),
  expiresAt: z.string(),
  isCurrent: z.boolean()
});
var createUserSchema = z.object({
  email: emailSchema,
  firstName: z.string().min(1, "Enter a first name").max(80),
  lastName: z.string().min(1, "Enter a last name").max(80),
  jobTitle: z.string().max(120).optional().nullable(),
  roles: z.array(z.enum(ROLE_KEYS)).min(1, "Assign at least one role").max(ROLE_KEYS.length),
  /** Send the invitation immediately. When false, the account is created dormant. */
  sendInvite: z.boolean().optional().default(true)
});
var updateUserSchema = z.object({
  firstName: z.string().min(1).max(80).optional(),
  lastName: z.string().min(1).max(80).optional(),
  jobTitle: z.string().max(120).optional().nullable()
});
var assignRolesSchema = z.object({
  roles: z.array(z.enum(ROLE_KEYS)).min(1, "A user must hold at least one role")
});
var userListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).optional().default(1),
  perPage: z.coerce.number().int().min(1).max(100).optional().default(25),
  search: z.string().max(120).optional(),
  status: z.enum(["ACTIVE", "INVITED", "SUSPENDED"]).optional(),
  role: z.enum(ROLE_KEYS).optional()
});
var userSummarySchema = z.object({
  id: z.string(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  jobTitle: z.string().nullable(),
  status: z.enum(["ACTIVE", "INVITED", "SUSPENDED"]),
  roles: z.array(z.enum(ROLE_KEYS)),
  lastLoginAt: z.string().nullable(),
  isLocked: z.boolean(),
  mustChangePassword: z.boolean(),
  createdAt: z.string()
});
var paginationMetaSchema = z.object({
  page: z.number(),
  perPage: z.number(),
  total: z.number(),
  totalPages: z.number()
});
var userListResponseSchema = z.object({
  data: z.array(userSummarySchema),
  meta: paginationMetaSchema
});
var loginAttemptSchema = z.object({
  id: z.string(),
  outcome: z.enum([
    "SUCCESS",
    "UNKNOWN_ACCOUNT",
    "BAD_PASSWORD",
    "ACCOUNT_LOCKED",
    "ACCOUNT_INACTIVE",
    "NO_PASSWORD_SET"
  ]),
  successful: z.boolean(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  createdAt: z.string()
});
var activeSessionSchema = z.object({
  /** The refresh-token family — one per signed-in device. */
  id: z.string(),
  userAgent: z.string().nullable(),
  ipAddress: z.string().nullable(),
  createdAt: z.string(),
  expiresAt: z.string(),
  isCurrent: z.boolean()
});
var userDetailSchema = userSummarySchema.extend({
  /** Null while an invitation is outstanding — the account has no password yet. */
  passwordChangedAt: z.string().nullable(),
  lockedUntil: z.string().nullable(),
  failedLoginCount: z.number(),
  lastFailedLoginAt: z.string().nullable(),
  activeSessionCount: z.number(),
  /** The public profile this login is linked to, when there is one. */
  teamMember: z.object({ id: z.string(), name: z.string(), slug: z.string() }).nullable(),
  updatedAt: z.string()
});
var BULK_USER_ACTIONS = ["suspend", "reactivate", "force-logout", "invite"];
var bulkUserActionSchema = z.object({
  action: z.enum(BULK_USER_ACTIONS),
  userIds: z.array(z.string().min(1)).min(1, "Select at least one account").max(100)
});
var bulkUserResultSchema = z.object({
  succeeded: z.array(z.string()),
  /** Per-account reasons, so a partial run says exactly what it skipped. */
  failed: z.array(z.object({ userId: z.string(), reason: z.string() }))
});
var SECTION_TYPES = [
  "hero",
  "heroSlider",
  "richText",
  "textImage",
  "servicesGrid",
  "featureCards",
  "statistics",
  "processSteps",
  "faqAccordion",
  "testimonials",
  "team",
  "articleListing",
  "consultationCta",
  "contactForm",
  "imageGallery",
  "riskDisclaimer",
  "motto"
];
var SECTION_TYPE_TO_ENUM = {
  hero: "HERO",
  heroSlider: "HERO_SLIDER",
  richText: "RICH_TEXT",
  textImage: "TEXT_IMAGE",
  servicesGrid: "SERVICES_GRID",
  featureCards: "FEATURE_CARDS",
  statistics: "STATISTICS",
  processSteps: "PROCESS_STEPS",
  faqAccordion: "FAQ_ACCORDION",
  testimonials: "TESTIMONIALS",
  team: "TEAM",
  articleListing: "ARTICLE_LIST",
  consultationCta: "CONSULTATION_CTA",
  contactForm: "CONTACT_FORM",
  imageGallery: "IMAGE_GALLERY",
  riskDisclaimer: "RISK_DISCLAIMER",
  motto: "MOTTO"
};
var ENUM_TO_SECTION_TYPE = Object.fromEntries(
  Object.entries(SECTION_TYPE_TO_ENUM).map(([key, value]) => [value, key])
);
var ALIGNMENTS = ["left", "center", "right"];
var BACKGROUNDS = ["white", "cream", "navy", "deepBlue", "goldTint", "image"];
var SPACINGS = ["none", "compact", "normal", "generous", "spacious"];
var CONTAINER_WIDTHS = ["narrow", "standard", "wide", "full"];
var CONTENT_ANIMATIONS = ["none", "fade", "sequence"];
var alignmentSchema = z.enum(ALIGNMENTS);
var backgroundSchema = z.enum(BACKGROUNDS);
var spacingSchema = z.enum(SPACINGS);
var containerWidthSchema = z.enum(CONTAINER_WIDTHS);
var contentAnimationSchema = z.enum(CONTENT_ANIMATIONS);
var buttonSchema = z.object({
  label: z.string().min(1, "Button text is required").max(60),
  href: z.string().min(1, "Button link is required").max(2048),
  variant: z.enum(["primary", "secondary", "ghost", "link"]).default("primary"),
  opensInNewTab: z.boolean().default(false)
});
var imageRefSchema = z.object({
  mediaId: z.string().min(1),
  alt: z.string().max(300).default(""),
  caption: z.string().max(300).optional()
});
var richTextSchema = z.object({
  type: z.literal("doc"),
  content: z.array(z.record(z.string(), z.unknown())).default([])
});
var emptyRichText = { type: "doc", content: [] };
var sectionSettingsSchema = z.object({
  alignment: alignmentSchema.default("left"),
  background: backgroundSchema.default("white"),
  /** Only meaningful when `background` is "image". */
  backgroundImage: imageRefSchema.optional(),
  /** 0–100. Darkens a background image so overlaid text stays readable. */
  backgroundOverlay: z.number().int().min(0).max(100).default(40),
  paddingTop: spacingSchema.default("normal"),
  paddingBottom: spacingSchema.default("normal"),
  containerWidth: containerWidthSchema.default("standard"),
  /**
   * Motion for the section's own prose. Blocks that animate their items
   * already — cards, statistics, process steps — are unaffected by this.
   */
  contentAnimation: contentAnimationSchema.default("none"),
  /** Anchor target, so navigation can link to this section directly. */
  anchorId: z.string().max(64).regex(/^$|^[a-z][a-z0-9-]*$/, "Use lowercase letters, numbers and hyphens").default(""),
  /** Extra classes for one-off adjustments. Escape hatch, used sparingly. */
  customClassName: z.string().max(200).default("")
});
var defaultSectionSettings = sectionSettingsSchema.parse({});

// src/dto/pages.ts
var PAGE_TEMPLATES = ["STANDARD", "LANDING", "FULL_WIDTH", "SIDEBAR", "LEGAL"];
var PAGE_STATUSES = ["DRAFT", "SCHEDULED", "PUBLISHED", "ARCHIVED"];
var slugSchema = z.string().min(1, "Enter a URL slug").max(120).regex(
  /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
  "Use lowercase letters, numbers and hyphens only \u2014 for example about-us"
);
var createPageSchema = z.object({
  title: z.string().min(1, "Enter a page title").max(200),
  /** Derived from the title when omitted. */
  slug: slugSchema.optional(),
  parentId: z.string().nullable().optional(),
  template: z.enum(PAGE_TEMPLATES).default("STANDARD"),
  adminNote: z.string().max(500).nullable().optional()
});
var updatePageSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  slug: slugSchema.optional(),
  parentId: z.string().nullable().optional(),
  template: z.enum(PAGE_TEMPLATES).optional(),
  adminNote: z.string().max(500).nullable().optional()
});
var duplicatePageSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  slug: slugSchema.optional()
});
var schedulePageSchema = z.object({
  scheduledFor: z.iso.datetime({ message: "Enter a valid date and time" })
});
var pageListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).optional().default(1),
  perPage: z.coerce.number().int().min(1).max(100).optional().default(25),
  search: z.string().max(200).optional(),
  status: z.enum(PAGE_STATUSES).optional(),
  template: z.enum(PAGE_TEMPLATES).optional()
});
var createSectionSchema = z.object({
  sectionType: z.enum(SECTION_TYPES),
  name: z.string().max(120).nullable().optional(),
  /** Validated against the registry for `sectionType`; defaults when omitted. */
  content: z.unknown().optional(),
  settings: sectionSettingsSchema.partial().optional(),
  /** Insert at this position; appended to the end when omitted. */
  position: z.number().int().min(0).optional()
});
var updateSectionSchema = z.object({
  name: z.string().max(120).nullable().optional(),
  content: z.unknown().optional(),
  settings: sectionSettingsSchema.partial().optional(),
  isVisible: z.boolean().optional()
});
var reorderSectionsSchema = z.object({
  sectionIds: z.array(z.string().min(1)).min(1, "Send the full ordered list of section IDs")
});
var toggleSectionVisibilitySchema = z.object({
  isVisible: z.boolean()
});
var seoMetadataSchema = z.object({
  metaTitle: z.string().max(70, "Titles over 70 characters are truncated in results").nullable().optional(),
  metaDescription: z.string().max(160, "Descriptions over 160 characters are truncated in results").nullable().optional(),
  canonicalUrl: z.union([z.url(), z.literal("")]).nullable().optional(),
  ogTitle: z.string().max(90).nullable().optional(),
  ogDescription: z.string().max(200).nullable().optional(),
  ogImageId: z.string().nullable().optional(),
  ogType: z.string().max(40).nullable().optional(),
  twitterCard: z.enum(["summary", "summary_large_image"]).nullable().optional(),
  noIndex: z.boolean().optional(),
  noFollow: z.boolean().optional(),
  keywords: z.array(z.string().max(60)).max(20).optional()
});
var savePageVersionSchema = z.object({
  label: z.string().max(120).optional()
});
var sectionResponseSchema = z.object({
  id: z.string(),
  pageId: z.string(),
  sectionType: z.enum(SECTION_TYPES),
  name: z.string().nullable(),
  content: z.unknown(),
  settings: sectionSettingsSchema,
  isVisible: z.boolean(),
  position: z.number(),
  updatedAt: z.string()
});
var pageSummarySchema = z.object({
  id: z.string(),
  title: z.string(),
  slug: z.string(),
  path: z.string(),
  template: z.enum(PAGE_TEMPLATES),
  status: z.enum(PAGE_STATUSES),
  isSystem: z.boolean(),
  publishedAt: z.string().nullable(),
  scheduledFor: z.string().nullable(),
  sectionCount: z.number(),
  updatedAt: z.string(),
  updatedBy: z.string().nullable()
});
var pageDetailSchema = pageSummarySchema.extend({
  adminNote: z.string().nullable(),
  parentId: z.string().nullable(),
  createdAt: z.string(),
  createdBy: z.string().nullable(),
  sections: z.array(sectionResponseSchema),
  seo: seoMetadataSchema.nullable(),
  /** True when the page has edits not present in the published version. */
  hasUnpublishedChanges: z.boolean()
});
var pageVersionSummarySchema = z.object({
  id: z.string(),
  versionNo: z.number(),
  label: z.string().nullable(),
  isPublished: z.boolean(),
  createdAt: z.string(),
  createdBy: z.string().nullable(),
  sectionCount: z.number()
});
var pageListResponseSchema = z.object({
  data: z.array(pageSummarySchema),
  meta: z.object({
    page: z.number(),
    perPage: z.number(),
    total: z.number(),
    totalPages: z.number()
  })
});
function slugify(input) {
  return input.normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 120).replace(/-+$/g, "");
}
function buildPagePath(slug, parentPath) {
  if (!slug) return "/";
  const parent = parentPath && parentPath !== "/" ? parentPath : "";
  return `${parent}/${slug}`;
}
var heroSliderSummarySchema = z.object({
  sectionId: z.string(),
  pageId: z.string(),
  pageTitle: z.string(),
  pagePath: z.string(),
  pageStatus: z.enum(PAGE_STATUSES),
  name: z.string().nullable(),
  isVisible: z.boolean(),
  slideCount: z.number(),
  /** Slides that would render right now, after visibility and scheduling. */
  liveSlideCount: z.number(),
  updatedAt: z.string()
});
var MEDIA_TYPES = ["IMAGE", "DOCUMENT", "VIDEO"];
var ACCEPTED_IMAGE_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/avif",
  "image/gif"
];
var ACCEPTED_DOCUMENT_MIME_TYPES = ["application/pdf"];
var IMAGE_VARIANT_WIDTHS = [400, 800, 1200, 1600];
var THUMBNAIL_WIDTH = 320;
var mediaVariantSchema = z.object({
  width: z.number(),
  height: z.number(),
  format: z.string(),
  key: z.string(),
  url: z.string(),
  sizeBytes: z.number(),
  /** True for the small derivative used in the library grid. */
  isThumbnail: z.boolean().optional()
});
var mediaItemSchema = z.object({
  id: z.string(),
  type: z.enum(MEDIA_TYPES),
  filename: z.string(),
  originalName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  /** Inline data URI placeholder, so images do not pop in on a slow connection. */
  blurhash: z.string().nullable(),
  altText: z.string().nullable(),
  caption: z.string().nullable(),
  credit: z.string().nullable(),
  folderId: z.string().nullable(),
  folderPath: z.string().nullable(),
  url: z.string(),
  thumbnailUrl: z.string(),
  variants: z.array(mediaVariantSchema),
  uploadedBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string()
});
var mediaListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).optional().default(1),
  perPage: z.coerce.number().int().min(1).max(100).optional().default(48),
  search: z.string().max(200).optional(),
  type: z.enum(MEDIA_TYPES).optional(),
  folderId: z.string().optional(),
  /** "root" restricts to items with no folder; omitted means every folder. */
  scope: z.enum(["all", "root", "folder"]).optional().default("all"),
  uploadedAfter: z.iso.datetime().optional(),
  uploadedBefore: z.iso.datetime().optional(),
  /** Only items missing alt text — the accessibility clean-up view. */
  missingAltText: z.coerce.boolean().optional(),
  sort: z.enum(["newest", "oldest", "name", "largest"]).optional().default("newest")
});
var mediaListResponseSchema = z.object({
  data: z.array(mediaItemSchema),
  meta: z.object({
    page: z.number(),
    perPage: z.number(),
    total: z.number(),
    totalPages: z.number()
  })
});
var updateMediaSchema = z.object({
  altText: z.string().max(300).nullable().optional(),
  caption: z.string().max(300).nullable().optional(),
  credit: z.string().max(200).nullable().optional(),
  folderId: z.string().nullable().optional(),
  filename: z.string().max(200).optional()
});
var createFolderSchema = z.object({
  name: z.string().min(1, "Enter a folder name").max(80).regex(/^[^/\\]+$/, "Folder names cannot contain slashes"),
  parentId: z.string().nullable().optional()
});
var folderSchema = z.object({
  id: z.string(),
  name: z.string(),
  path: z.string(),
  parentId: z.string().nullable(),
  mediaCount: z.number()
});
var mediaUsageEntrySchema = z.object({
  /** Where the reference lives, e.g. "Page section" or "Article cover image". */
  context: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  entityLabel: z.string(),
  /** Admin route for the record holding the reference, when one exists. */
  editUrl: z.string().nullable()
});
var mediaUsageSchema = z.object({
  mediaId: z.string(),
  totalReferences: z.number(),
  entries: z.array(mediaUsageEntrySchema)
});
function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function safeFilename(originalName) {
  const lastDot = originalName.lastIndexOf(".");
  const rawBase = lastDot > 0 ? originalName.slice(0, lastDot) : originalName;
  const rawExtension = lastDot > 0 ? originalName.slice(lastDot + 1) : "";
  const base = rawBase.normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80) || "file";
  const extension = rawExtension.toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 8);
  return { base, extension };
}
var SERVICE_STATUSES = ["DRAFT", "SCHEDULED", "PUBLISHED", "ARCHIVED"];
var serviceAudienceItemSchema = z.object({
  title: z.string().min(1, "Enter who this is for").max(120),
  description: z.string().max(400).default(""),
  icon: z.string().max(60).default("")
});
var serviceAudienceSchema = z.array(serviceAudienceItemSchema).max(8).default([]);
var serviceInclusionSchema = z.object({
  label: z.string().min(1, "Enter what is included").max(140),
  detail: z.string().max(300).default(""),
  /** False marks something explicitly *not* included, which builds more trust
   *  than an all-positive list and heads off the awkward conversation later. */
  isIncluded: z.boolean().default(true)
});
var serviceInclusionsSchema = z.array(serviceInclusionSchema).max(20).default([]);
var serviceCtaSchema = z.object({
  heading: z.string().max(160).default("Ready to talk?"),
  body: z.string().max(400).default(""),
  primaryButton: buttonSchema.default({
    label: "Book a consultation",
    href: "/book-consultation",
    variant: "primary",
    opensInNewTab: false
  }),
  secondaryButton: buttonSchema.optional()
});
var benefitSchema = z.object({
  id: z.string().optional(),
  title: z.string().min(1, "Enter a benefit title").max(120),
  description: z.string().max(400).default(""),
  icon: z.string().max(60).default("")
});
var processStepInputSchema = z.object({
  id: z.string().optional(),
  title: z.string().min(1, "Enter a step title").max(120),
  description: z.string().max(400).default(""),
  icon: z.string().max(60).default(""),
  duration: z.string().max(40).default("")
});
var createServiceSchema = z.object({
  name: z.string().min(1, "Enter a service name").max(160),
  slug: slugSchema.optional(),
  shortName: z.string().max(60).nullable().optional(),
  summary: z.string().min(1, "Enter a short summary").max(400),
  icon: z.string().max(60).nullable().optional()
});
var updateServiceSchema = z.object({
  name: z.string().min(1).max(160).optional(),
  slug: slugSchema.optional(),
  shortName: z.string().max(60).nullable().optional(),
  summary: z.string().min(1).max(400).optional(),
  description: richTextSchema.optional(),
  introduction: richTextSchema.optional(),
  icon: z.string().max(60).nullable().optional(),
  accentColor: z.string().max(20).nullable().optional(),
  minInvestment: z.string().max(80).nullable().optional(),
  heroImage: imageRefSchema.nullable().optional(),
  cardImage: imageRefSchema.nullable().optional(),
  audience: serviceAudienceSchema.optional(),
  inclusions: serviceInclusionsSchema.optional(),
  cta: serviceCtaSchema.optional(),
  riskDisclaimer: z.string().max(2e3).nullable().optional(),
  isFeatured: z.boolean().optional()
});
var reorderServicesSchema = z.object({
  serviceIds: z.array(z.string().min(1)).min(1, "Send the full ordered list of service IDs")
});
var replaceBenefitsSchema = z.object({
  benefits: z.array(benefitSchema).max(12)
});
var replaceProcessStepsSchema = z.object({
  steps: z.array(processStepInputSchema).max(10)
});
var linkFaqsSchema = z.object({
  faqIds: z.array(z.string()).max(30)
});
var serviceListQuerySchema = z.object({
  status: z.enum(SERVICE_STATUSES).optional(),
  search: z.string().max(200).optional()
});
var serviceSummarySchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  shortName: z.string().nullable(),
  summary: z.string(),
  icon: z.string().nullable(),
  accentColor: z.string().nullable(),
  minInvestment: z.string().nullable(),
  status: z.enum(SERVICE_STATUSES),
  sortOrder: z.number(),
  isFeatured: z.boolean(),
  cardImageUrl: z.string().nullable(),
  benefitCount: z.number(),
  stepCount: z.number(),
  faqCount: z.number(),
  updatedAt: z.string()
});
var imageResponseSchema = z.object({
  mediaId: z.string(),
  url: z.string(),
  alt: z.string(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  placeholder: z.string().nullable()
});
var serviceDetailSchema = serviceSummarySchema.extend({
  description: richTextSchema.nullable(),
  introduction: richTextSchema.nullable(),
  heroImage: imageResponseSchema.nullable(),
  cardImage: imageResponseSchema.nullable(),
  audience: serviceAudienceSchema,
  inclusions: serviceInclusionsSchema,
  cta: serviceCtaSchema,
  riskDisclaimer: z.string().nullable(),
  benefits: z.array(benefitSchema.extend({ id: z.string() })),
  processSteps: z.array(processStepInputSchema.extend({ id: z.string(), stepNumber: z.number() })),
  faqs: z.array(
    z.object({
      id: z.string(),
      question: z.string(),
      answer: richTextSchema,
      categoryLabel: z.string().nullable()
    })
  ),
  publishedAt: z.string().nullable(),
  createdAt: z.string(),
  /**
   * SEO overrides, or null when nobody has filled the panel in. The admin
   * needs to read these back — a form that can only be written to is a form
   * an editor cannot trust.
   */
  seo: z.object({
    metaTitle: z.string().nullable(),
    metaDescription: z.string().nullable(),
    ogTitle: z.string().nullable(),
    ogDescription: z.string().nullable(),
    ogImageId: z.string().nullable(),
    canonicalUrl: z.string().nullable(),
    noIndex: z.boolean()
  }).nullable()
});
var publicServiceSchema = serviceDetailSchema.extend({
  relatedPosts: z.array(
    z.object({
      id: z.string(),
      slug: z.string(),
      title: z.string(),
      excerpt: z.string().nullable(),
      publishedAt: z.string().nullable(),
      readingMinutes: z.number().nullable(),
      coverImageUrl: z.string().nullable(),
      author: z.string().nullable()
    })
  ),
  testimonials: z.array(
    z.object({
      id: z.string(),
      quote: z.string(),
      authorName: z.string(),
      authorTitle: z.string().nullable(),
      authorCompany: z.string().nullable()
    })
  ),
  seo: z.object({
    metaTitle: z.string().nullable(),
    metaDescription: z.string().nullable(),
    ogTitle: z.string().nullable(),
    ogDescription: z.string().nullable(),
    ogImageUrl: z.string().nullable(),
    canonicalUrl: z.string().nullable(),
    noIndex: z.boolean()
  }).nullable()
});
var defaultServiceCta = serviceCtaSchema.parse({});
var emptyServiceRichText = emptyRichText;
var POST_STATUSES = ["DRAFT", "SCHEDULED", "PUBLISHED", "ARCHIVED"];
var createPostSchema = z.object({
  title: z.string().min(1, "Enter an article title").max(200),
  slug: slugSchema.optional(),
  excerpt: z.string().max(400).nullable().optional(),
  content: richTextSchema.default(emptyRichText),
  categoryId: z.string().nullable().optional(),
  authorId: z.string().nullable().optional(),
  coverImageId: z.string().nullable().optional(),
  tagIds: z.array(z.string()).max(12).default([]),
  isFeatured: z.boolean().default(false)
});
var updatePostSchema = z.object({
  title: z.string().min(1, "Enter an article title").max(200).optional(),
  slug: slugSchema.optional(),
  excerpt: z.string().max(400).nullable().optional(),
  content: richTextSchema.optional(),
  categoryId: z.string().nullable().optional(),
  authorId: z.string().nullable().optional(),
  coverImageId: z.string().nullable().optional(),
  tagIds: z.array(z.string()).max(12).optional(),
  isFeatured: z.boolean().optional()
});
var schedulePostSchema = z.object({
  scheduledFor: z.iso.datetime({ message: "Enter a valid date and time" })
});
var postListQuerySchema = z.object({
  status: z.enum(POST_STATUSES).optional(),
  categoryId: z.string().optional(),
  authorId: z.string().optional(),
  search: z.string().max(200).optional(),
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(100).default(20)
});
var upsertCategorySchema = z.object({
  name: z.string().min(1, "Enter a category name").max(120),
  slug: slugSchema.optional(),
  description: z.string().max(500).nullable().optional(),
  sortOrder: z.number().int().min(0).max(999).optional()
});
var upsertTagSchema = z.object({
  name: z.string().min(1, "Enter a tag name").max(80),
  slug: slugSchema.optional()
});
var categorySummarySchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  sortOrder: z.number(),
  postCount: z.number()
});
var tagSummarySchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  postCount: z.number()
});
var postSummarySchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  excerpt: z.string().nullable(),
  status: z.enum(POST_STATUSES),
  publishedAt: z.string().nullable(),
  scheduledFor: z.string().nullable(),
  readingMinutes: z.number().nullable(),
  viewCount: z.number(),
  isFeatured: z.boolean(),
  coverImageUrl: z.string().nullable(),
  category: z.object({ id: z.string(), name: z.string() }).nullable(),
  author: z.object({ id: z.string(), name: z.string() }).nullable(),
  tags: z.array(z.object({ id: z.string(), name: z.string() })),
  updatedAt: z.string(),
  updatedBy: z.string().nullable()
});
var postDetailSchema = postSummarySchema.extend({
  content: richTextSchema,
  contentHtml: z.string().nullable(),
  coverImageId: z.string().nullable(),
  categoryId: z.string().nullable(),
  authorId: z.string().nullable(),
  seo: z.object({
    metaTitle: z.string().nullable(),
    metaDescription: z.string().nullable(),
    ogTitle: z.string().nullable(),
    ogDescription: z.string().nullable(),
    ogImageId: z.string().nullable(),
    canonicalUrl: z.string().nullable(),
    noIndex: z.boolean()
  }).nullable(),
  createdAt: z.string()
});
var postListResponseSchema = z.object({
  data: z.array(postSummarySchema),
  meta: z.object({
    page: z.number(),
    perPage: z.number(),
    total: z.number(),
    totalPages: z.number()
  })
});
var postEditorOptionsSchema = z.object({
  categories: z.array(categorySummarySchema),
  tags: z.array(tagSummarySchema),
  authors: z.array(z.object({ id: z.string(), name: z.string(), role: z.string() }))
});
var SERVICE_INTERESTS = [
  "STOCK_ADVISORY",
  "RETIREMENT_PLANNING",
  "FINANCIAL_COACHING",
  "GENERAL"
];
var SERVICE_INTEREST_LABELS = {
  STOCK_ADVISORY: "Offshore and Local Stock Advisory",
  RETIREMENT_PLANNING: "Retirement Planning",
  FINANCIAL_COACHING: "Financial Coaching",
  GENERAL: "Not sure yet"
};
var CONSULTATION_MODES = ["PHONE_CALL", "WHATSAPP", "VIDEO_CALL", "IN_PERSON"];
var CONSULTATION_MODE_LABELS = {
  PHONE_CALL: "Phone call",
  WHATSAPP: "WhatsApp",
  VIDEO_CALL: "Video call",
  IN_PERSON: "In person"
};
var LEAD_STATUSES = [
  "NEW",
  "IN_REVIEW",
  "CONTACTED",
  "BOOKED",
  "COMPLETED",
  "CLOSED",
  "SPAM"
];
var LEAD_STATUS_LABELS = {
  NEW: "New",
  IN_REVIEW: "In review",
  CONTACTED: "Contacted",
  BOOKED: "Booked",
  COMPLETED: "Completed",
  CLOSED: "Closed",
  SPAM: "Spam"
};
var MESSAGE_STATUSES = ["UNREAD", "READ", "REPLIED", "ARCHIVED", "SPAM"];
var MESSAGE_STATUS_LABELS = {
  UNREAD: "Unread",
  READ: "Read",
  REPLIED: "Replied",
  ARCHIVED: "Archived",
  SPAM: "Spam"
};
var phoneSchema = z.string().min(7, "Enter a valid phone number").max(24).regex(/^[+()\d\s-]+$/, "Enter a valid phone number");
var spamGuardSchema = z.object({
  website: z.string().max(200).optional(),
  formLoadedAt: z.number().int().optional(),
  turnstileToken: z.string().max(4096).optional()
});
var createConsultationSchema = spamGuardSchema.extend({
  fullName: z.string().min(2, "Enter your full name").max(160),
  email: emailSchema,
  phone: phoneSchema,
  whatsappNumber: z.union([phoneSchema, z.literal("")]).optional().transform((value) => value || void 0),
  serviceInterest: z.enum(SERVICE_INTERESTS),
  /** Date only, so a visitor is not asked to reason about time zones. */
  preferredDate: z.union([z.iso.date(), z.literal("")]).optional().transform((value) => value || void 0),
  preferredTimeSlot: z.union([z.enum(["MORNING", "MIDDAY", "AFTERNOON", "EVENING"]), z.literal("")]).optional().transform((value) => value || void 0),
  preferredChannel: z.enum(CONSULTATION_MODES),
  message: z.string().max(2e3).optional(),
  /**
   * Explicit, unticked by default, and required.
   *
   * The Kenyan Data Protection Act needs consent to be freely given and
   * unambiguous. A pre-ticked box is neither, so `literal(true)` refuses the
   * submission rather than inferring agreement from silence.
   */
  consent: z.literal(true, {
    message: "Please confirm you consent to us storing and using these details"
  })
});
var TIME_SLOT_LABELS = {
  MORNING: "Morning (8:30am \u2013 11am)",
  MIDDAY: "Midday (11am \u2013 2pm)",
  AFTERNOON: "Afternoon (2pm \u2013 5pm)",
  EVENING: "Evening (after 5pm)"
};
var createContactMessageSchema = spamGuardSchema.extend({
  name: z.string().min(2, "Enter your name").max(160),
  email: emailSchema,
  phone: z.union([phoneSchema, z.literal("")]).optional().transform((value) => value || void 0),
  serviceInterest: z.enum(SERVICE_INTERESTS).default("GENERAL"),
  message: z.string().min(10, "Tell us a little about your enquiry").max(4e3),
  consent: z.literal(true, {
    message: "Please confirm you consent to us storing and using these details"
  })
});
var leadSubmissionResponseSchema = z.object({
  referenceCode: z.string().optional(),
  message: z.string()
});
var consultationListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).optional().default(1),
  perPage: z.coerce.number().int().min(1).max(100).optional().default(25),
  search: z.string().max(200).optional(),
  status: z.enum(LEAD_STATUSES).optional(),
  serviceInterest: z.enum(SERVICE_INTERESTS).optional(),
  assignedToId: z.string().optional(),
  /** "unassigned" surfaces the queue nobody has picked up. */
  assignment: z.enum(["any", "mine", "unassigned"]).optional().default("any"),
  submittedAfter: z.iso.datetime().optional(),
  submittedBefore: z.iso.datetime().optional(),
  sort: z.enum(["newest", "oldest"]).optional().default("newest")
});
var updateLeadStatusSchema = z.object({
  status: z.enum(LEAD_STATUSES),
  /** Recorded on the timeline, so a status change carries its reasoning. */
  note: z.string().max(1e3).optional()
});
var assignLeadSchema = z.object({
  assignedToId: z.string().nullable()
});
var addInternalNoteSchema = z.object({
  note: z.string().min(1, "Enter a note").max(2e3)
});
var internalNoteSchema = z.object({
  authorId: z.string().nullable(),
  authorName: z.string(),
  note: z.string(),
  at: z.string()
});
var consultationSummarySchema = z.object({
  id: z.string(),
  referenceCode: z.string(),
  fullName: z.string(),
  email: z.string(),
  phone: z.string(),
  whatsappNumber: z.string().nullable(),
  serviceInterest: z.enum(SERVICE_INTERESTS),
  preferredChannel: z.enum(CONSULTATION_MODES).nullable(),
  preferredDate: z.string().nullable(),
  preferredTimeSlot: z.string().nullable(),
  status: z.enum(LEAD_STATUSES),
  assignedTo: z.object({ id: z.string(), name: z.string() }).nullable(),
  createdAt: z.string(),
  respondedAt: z.string().nullable()
});
var consultationDetailSchema = consultationSummarySchema.extend({
  message: z.string().nullable(),
  source: z.string().nullable(),
  consentedAt: z.string(),
  internalNotes: z.array(internalNoteSchema),
  /** Merged notes and audit entries, newest first. */
  activity: z.array(
    z.object({
      type: z.enum(["note", "event"]),
      label: z.string(),
      detail: z.string().nullable(),
      actor: z.string().nullable(),
      at: z.string()
    })
  )
});
var consultationListResponseSchema = z.object({
  data: z.array(consultationSummarySchema),
  meta: z.object({
    page: z.number(),
    perPage: z.number(),
    total: z.number(),
    totalPages: z.number()
  }),
  /** Counts per status, for the filter chips. */
  counts: z.record(z.string(), z.number())
});
var contactMessageListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).optional().default(1),
  perPage: z.coerce.number().int().min(1).max(100).optional().default(25),
  search: z.string().max(200).optional(),
  status: z.enum(MESSAGE_STATUSES).optional(),
  serviceInterest: z.enum(SERVICE_INTERESTS).optional()
});
var contactMessageSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  serviceInterest: z.enum(SERVICE_INTERESTS),
  subject: z.string().nullable(),
  message: z.string(),
  status: z.enum(MESSAGE_STATUSES),
  assignedTo: z.object({ id: z.string(), name: z.string() }).nullable(),
  consentedAt: z.string(),
  repliedAt: z.string().nullable(),
  createdAt: z.string()
});
var updateMessageStatusSchema = z.object({
  status: z.enum(MESSAGE_STATUSES)
});
var SETTING_TYPES = ["STRING", "TEXT", "NUMBER", "BOOLEAN", "JSON", "MEDIA"];
var SETTING_GROUPS = [
  "general",
  "contact",
  "social",
  "legal",
  "seo",
  "integrations"
];
var SETTING_GROUP_LABELS = {
  general: "General",
  contact: "Contact",
  social: "Social",
  legal: "Legal",
  seo: "SEO",
  integrations: "Integrations"
};
var settingSchema = z.object({
  key: z.string(),
  group: z.string(),
  label: z.string(),
  description: z.string().nullable(),
  type: z.enum(SETTING_TYPES),
  value: z.unknown(),
  isPublic: z.boolean(),
  sortOrder: z.number(),
  updatedAt: z.string()
});
var updateSettingsSchema = z.object({
  settings: z.array(z.object({ key: z.string().min(1), value: z.unknown() })).min(1, "Nothing to save").max(100)
});
var MENU_LOCATIONS = [
  "HEADER",
  "FOOTER_PRIMARY",
  "FOOTER_SECONDARY",
  "MOBILE",
  "LEGAL"
];
var MENU_LOCATION_LABELS = {
  HEADER: "Header",
  FOOTER_PRIMARY: "Footer \u2014 first column",
  FOOTER_SECONDARY: "Footer \u2014 second column",
  MOBILE: "Mobile only",
  LEGAL: "Legal strip"
};
var NAV_LINK_TYPES = ["PAGE", "SERVICE", "POST", "EXTERNAL", "ANCHOR"];
var NAV_LINK_TYPE_LABELS = {
  PAGE: "A page",
  SERVICE: "A service",
  POST: "An article",
  EXTERNAL: "A web address",
  ANCHOR: "A section on this page"
};
var navigationItemSchema = z.object({
  id: z.string(),
  parentId: z.string().nullable(),
  label: z.string(),
  linkType: z.enum(NAV_LINK_TYPES),
  pageId: z.string().nullable(),
  serviceId: z.string().nullable(),
  url: z.string().nullable(),
  anchor: z.string().nullable(),
  icon: z.string().nullable(),
  description: z.string().nullable(),
  opensInNewTab: z.boolean(),
  isVisible: z.boolean(),
  sortOrder: z.number(),
  /** Resolved for the admin list, so a broken target is visible at a glance. */
  resolvedHref: z.string().nullable()
});
var navigationMenuSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  location: z.enum(MENU_LOCATIONS),
  items: z.array(navigationItemSchema.extend({ children: z.array(navigationItemSchema) }))
});
var linkTargetShape = {
  linkType: z.enum(NAV_LINK_TYPES),
  pageId: z.string().nullable().optional(),
  serviceId: z.string().nullable().optional(),
  url: z.string().max(2048).nullable().optional(),
  anchor: z.string().max(64).regex(/^$|^[a-z][a-z0-9-]*$/, "Use lowercase letters, numbers and hyphens").nullable().optional()
};
var targetRefinement = (schema) => schema.refine(
  (value) => {
    const item = value;
    if (item.linkType === "PAGE") return Boolean(item.pageId);
    if (item.linkType === "SERVICE") return Boolean(item.serviceId);
    if (item.linkType === "EXTERNAL" || item.linkType === "POST") return Boolean(item.url);
    return true;
  },
  { message: "Choose where this link should go", path: ["linkType"] }
);
var createNavigationItemSchema = targetRefinement(
  z.object({
    menuId: z.string().min(1),
    parentId: z.string().nullable().optional(),
    label: z.string().min(1, "Enter a label").max(80),
    ...linkTargetShape,
    icon: z.string().max(60).nullable().optional(),
    description: z.string().max(200).nullable().optional(),
    opensInNewTab: z.boolean().default(false),
    isVisible: z.boolean().default(true)
  })
);
var updateNavigationItemSchema = z.object({
  label: z.string().min(1).max(80).optional(),
  linkType: z.enum(NAV_LINK_TYPES).optional(),
  pageId: z.string().nullable().optional(),
  serviceId: z.string().nullable().optional(),
  url: z.string().max(2048).nullable().optional(),
  anchor: z.string().max(64).nullable().optional(),
  icon: z.string().max(60).nullable().optional(),
  description: z.string().max(200).nullable().optional(),
  opensInNewTab: z.boolean().optional(),
  isVisible: z.boolean().optional(),
  parentId: z.string().nullable().optional()
});
var reorderNavigationSchema = z.object({
  /** The complete ordered list for one parent, top level when null. */
  parentId: z.string().nullable(),
  itemIds: z.array(z.string()).min(1)
});
var navigationTargetsSchema = z.object({
  pages: z.array(z.object({ id: z.string(), title: z.string(), path: z.string() })),
  services: z.array(z.object({ id: z.string(), name: z.string(), slug: z.string() }))
});
var pathSchema = z.string().min(1, "Enter a path").max(2048).regex(/^\//, "Start the path with a slash, for example /old-page");
var redirectSchema = z.object({
  id: z.string(),
  fromPath: z.string(),
  toPath: z.string(),
  statusCode: z.number(),
  isActive: z.boolean(),
  hitCount: z.number(),
  lastHitAt: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.string()
});
var redirectFields = z.object({
  fromPath: pathSchema,
  toPath: z.string().min(1, "Enter a destination").max(2048),
  /** 301 is permanent and cached hard by browsers; 302 is temporary. */
  statusCode: z.union([z.literal(301), z.literal(302)]).default(301),
  isActive: z.boolean().default(true),
  note: z.string().max(500).nullable().optional()
});
var notSelfReferential = (value) => !value.fromPath || !value.toPath || value.fromPath !== value.toPath;
var upsertRedirectSchema = redirectFields.refine(notSelfReferential, {
  message: "A redirect cannot point at itself",
  path: ["toPath"]
});
var updateRedirectSchema = redirectFields.partial().refine(notSelfReferential, {
  message: "A redirect cannot point at itself",
  path: ["toPath"]
});
var PUBLISH_STATUSES = ["DRAFT", "SCHEDULED", "PUBLISHED", "ARCHIVED"];
var teamMemberSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  role: z.string(),
  bio: richTextSchema.nullable(),
  credentials: z.array(z.string()),
  email: z.string().nullable(),
  linkedinUrl: z.string().nullable(),
  photoId: z.string().nullable(),
  photoUrl: z.string().nullable(),
  userId: z.string().nullable(),
  status: z.enum(PUBLISH_STATUSES),
  sortOrder: z.number(),
  postCount: z.number(),
  updatedAt: z.string()
});
var upsertTeamMemberSchema = z.object({
  name: z.string().min(1, "Enter a name").max(160),
  slug: slugSchema.optional(),
  role: z.string().min(1, "Enter a role").max(160),
  bio: richTextSchema.default(emptyRichText),
  /** Post-nominals such as CFA or CPA(K), one per entry. */
  credentials: z.array(z.string().max(40)).max(8).default([]),
  email: z.string().max(255).nullable().optional(),
  linkedinUrl: z.string().max(2048).nullable().optional(),
  photoId: z.string().nullable().optional(),
  /** Links the public profile to a login, so bylines resolve to an account. */
  userId: z.string().nullable().optional()
});
var faqSchema = z.object({
  id: z.string(),
  question: z.string(),
  answer: richTextSchema,
  categoryLabel: z.string().nullable(),
  serviceId: z.string().nullable(),
  serviceName: z.string().nullable(),
  status: z.enum(PUBLISH_STATUSES),
  sortOrder: z.number(),
  updatedAt: z.string()
});
var upsertFaqSchema = z.object({
  question: z.string().min(1, "Enter a question").max(300),
  answer: richTextSchema.default(emptyRichText),
  /** Free-text grouping in the admin, e.g. "Getting started". */
  categoryLabel: z.string().max(80).nullable().optional(),
  /** When set, the FAQ appears on that service's page. */
  serviceId: z.string().nullable().optional()
});
var testimonialSchema = z.object({
  id: z.string(),
  quote: z.string(),
  authorName: z.string(),
  authorTitle: z.string().nullable(),
  authorCompany: z.string().nullable(),
  rating: z.number().nullable(),
  serviceId: z.string().nullable(),
  serviceName: z.string().nullable(),
  avatarId: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  consentGivenAt: z.string().nullable(),
  consentNote: z.string().nullable(),
  status: z.enum(PUBLISH_STATUSES),
  isFeatured: z.boolean(),
  sortOrder: z.number(),
  updatedAt: z.string()
});
var upsertTestimonialSchema = z.object({
  quote: z.string().min(1, "Enter the quote").max(1e3),
  authorName: z.string().min(1, "Enter who said it").max(160),
  authorTitle: z.string().max(160).nullable().optional(),
  authorCompany: z.string().max(160).nullable().optional(),
  rating: z.number().int().min(1).max(5).nullable().optional(),
  serviceId: z.string().nullable().optional(),
  avatarId: z.string().nullable().optional(),
  isFeatured: z.boolean().default(false)
});
var recordConsentSchema = z.object({
  consentNote: z.string().min(1, "Record how consent was given \u2014 this is the evidence trail").max(500),
  /** When consent was actually given, if not now. */
  consentGivenAt: z.iso.datetime().optional()
});
var reorderSchema = z.object({
  ids: z.array(z.string()).min(1, "Send the full ordered list")
});
var AUDIT_ACTIONS = [
  "CREATE",
  "UPDATE",
  "DELETE",
  "RESTORE",
  "PUBLISH",
  "UNPUBLISH",
  "LOGIN",
  "LOGIN_FAILED",
  "LOGOUT",
  "PASSWORD_RESET",
  "ROLE_CHANGE",
  "EXPORT"
];
var AUDIT_ACTION_LABELS = {
  CREATE: "Created",
  UPDATE: "Updated",
  DELETE: "Deleted",
  RESTORE: "Restored",
  PUBLISH: "Published",
  UNPUBLISH: "Unpublished",
  LOGIN: "Signed in",
  LOGIN_FAILED: "Sign-in failed",
  LOGOUT: "Signed out",
  PASSWORD_RESET: "Password reset",
  ROLE_CHANGE: "Roles changed",
  EXPORT: "Exported data"
};
var auditLogQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(100).default(50),
  action: z.enum(AUDIT_ACTIONS).optional(),
  entityType: z.string().max(60).optional(),
  entityId: z.string().max(60).optional(),
  userId: z.string().max(60).optional(),
  search: z.string().max(200).optional(),
  after: z.iso.datetime().optional(),
  before: z.iso.datetime().optional()
});
var auditLogEntrySchema = z.object({
  id: z.string(),
  action: z.enum(AUDIT_ACTIONS),
  entityType: z.string(),
  entityId: z.string().nullable(),
  entityLabel: z.string().nullable(),
  before: z.unknown(),
  after: z.unknown(),
  actor: z.object({ id: z.string(), name: z.string(), email: z.string() }).nullable(),
  ipAddress: z.string().nullable(),
  requestId: z.string().nullable(),
  createdAt: z.string()
});
var auditLogResponseSchema = z.object({
  data: z.array(auditLogEntrySchema),
  meta: z.object({
    page: z.number(),
    perPage: z.number(),
    total: z.number(),
    totalPages: z.number()
  }),
  /** Distinct entity types present, for the filter dropdown. */
  entityTypes: z.array(z.string())
});
var headingSchema = z.string().max(200).default("");
var eyebrowSchema = z.string().max(80).default("");
var subheadingSchema = z.string().max(400).default("");
var heroContentSchema = z.object({
  eyebrow: eyebrowSchema,
  heading: z.string().max(200).default("Your Vision. Our Expertise."),
  subheading: subheadingSchema,
  image: imageRefSchema.optional(),
  buttons: z.array(buttonSchema).max(2).default([]),
  /** Small trust markers under the buttons, e.g. "CFA-led research". */
  highlights: z.array(z.string().max(80)).max(4).default([]),
  layout: z.enum(["centered", "split", "imageRight", "imageLeft"]).default("centered"),
  height: z.enum(["compact", "standard", "tall", "fullscreen"]).default("standard"),
  /**
   * How the photograph is treated behind the copy.
   *
   * The same three controls the slider gives an editor per slide, for the same
   * reasons — a hero with a background image has exactly the same problem to
   * solve, and there was no argument for it being the one block that could not
   * solve it. On a split layout the image sits beside the copy rather than
   * under it, so `overlayOpacity` has nothing to darken and is ignored.
   */
  /** Darkens the photograph so overlaid text stays readable. 0–100. */
  overlayOpacity: z.number().int().min(0).max(100).default(55),
  /** Where the focal point sits when the image is cropped to the frame. */
  focalPoint: z.enum(["center", "top", "bottom", "left", "right"]).default("center"),
  /**
   * Mirror the photograph horizontally.
   *
   * Purely compositional: hero copy sits on the left, so a subject looking or
   * leading out of frame to the left pulls the eye away from the words. Flipping
   * turns them back into the text. Costs nothing — it is a CSS transform on the
   * same file, not a second asset.
   *
   * Not for images containing text, logos, signage or anything handed — mirrored
   * writing is unreadable and a mirrored wedding ring is a different person.
   */
  flipHorizontal: z.boolean().default(false)
});
var heroSlideSchema = z.object({
  image: imageRefSchema,
  eyebrow: eyebrowSchema,
  heading: z.string().min(1, "Enter a headline for this slide").max(200),
  subheading: subheadingSchema,
  /** Optional line under the image, e.g. a location or attribution. */
  caption: z.string().max(160).default(""),
  buttons: z.array(buttonSchema).max(2).default([]),
  /** Darkens the photograph so overlaid text stays readable. 0–100. */
  overlayOpacity: z.number().int().min(0).max(100).default(55),
  /** Where the focal point sits when the image is cropped to the frame. */
  focalPoint: z.enum(["center", "top", "bottom", "left", "right"]).default("center"),
  /**
   * Mirror the photograph horizontally.
   *
   * Purely compositional: hero copy sits on the left, so a subject looking or
   * leading out of frame to the left pulls the eye away from the words. Flipping
   * turns them back into the text. Costs nothing — it is a CSS transform on the
   * same file, not a second asset.
   *
   * Not for images containing text, logos, signage or anything handed — mirrored
   * writing is unreadable and a mirrored wedding ring is a different person.
   */
  flipHorizontal: z.boolean().default(false),
  /**
   * Hidden without being deleted.
   *
   * A campaign slide is usually reused. Deleting it to take it down means
   * rebuilding it — and its alt text, and its buttons — the next time it runs.
   */
  isVisible: z.boolean().default(true),
  /**
   * Optional run window. Null means "live as soon as it is visible, until
   * somebody takes it down".
   *
   * Scheduling belongs on the slide rather than the section because a campaign
   * slide and the evergreen slide behind it share one slider: the campaign
   * should retire itself at midnight without anybody logging in to do it.
   */
  startsAt: z.iso.datetime().nullable().default(null),
  endsAt: z.iso.datetime().nullable().default(null)
});
function isSlideLive(slide, now = /* @__PURE__ */ new Date()) {
  if (!slide.isVisible) return false;
  if (slide.startsAt && new Date(slide.startsAt) > now) return false;
  if (slide.endsAt && new Date(slide.endsAt) <= now) return false;
  return true;
}
var heroSliderContentSchema = z.object({
  slides: z.array(heroSlideSchema).max(8).default([]),
  /** Small trust markers under the slide copy, shared by every slide. */
  highlights: z.array(z.string().max(80)).max(4).default([]),
  height: z.enum(["standard", "tall", "fullscreen"]).default("tall"),
  /**
   * Seconds between slides. Zero advances only on interaction.
   *
   * Eight seconds by default: long enough to read a headline and its
   * supporting line without hurrying, short enough that a visitor who is not
   * reading still sees the second slide.
   *
   * WCAG 2.2.2 requires any auto-moving content to be pausable, and the
   * renderer honours that — it stops on hover, stops on focus, exposes a
   * visible stop control, and does not start at all for a visitor who has
   * asked for reduced motion. Set this to zero to turn it off entirely.
   */
  autoplaySeconds: z.number().int().min(0).max(30).default(8),
  showArrows: z.boolean().default(true),
  showDots: z.boolean().default(true)
});
var richTextContentSchema = z.object({
  heading: headingSchema,
  body: richTextSchema.default(emptyRichText)
});
var textImageContentSchema = z.object({
  eyebrow: eyebrowSchema,
  heading: headingSchema,
  body: richTextSchema.default(emptyRichText),
  image: imageRefSchema.optional(),
  imagePosition: z.enum(["left", "right"]).default("right"),
  imageRatio: z.enum(["square", "portrait", "landscape", "auto"]).default("landscape"),
  buttons: z.array(buttonSchema).max(2).default([])
});
var servicesGridContentSchema = z.object({
  eyebrow: eyebrowSchema,
  heading: headingSchema,
  intro: subheadingSchema,
  /** Empty means "every published service", which is what an editor expects. */
  serviceIds: z.array(z.string()).max(12).default([]),
  columns: z.union([z.literal(2), z.literal(3), z.literal(4)]).default(3),
  showIcon: z.boolean().default(true),
  showSummary: z.boolean().default(true),
  showLink: z.boolean().default(true),
  linkLabel: z.string().max(40).default("Learn more")
});
var featureCardSchema = z.object({
  title: z.string().min(1, "Card title is required").max(120),
  description: z.string().max(400).default(""),
  icon: z.string().max(60).default(""),
  image: imageRefSchema.optional(),
  link: buttonSchema.partial().optional()
});
var featureCardsContentSchema = z.object({
  eyebrow: eyebrowSchema,
  heading: headingSchema,
  intro: subheadingSchema,
  cards: z.array(featureCardSchema).max(12).default([]),
  /**
   * Five and six exist here and not on the other grids for a reason: this is
   * the block used for short, parallel statements — a set of values, a set of
   * principles — where the whole point is that they are read as one line of
   * equals. Five values wrapping to 3 + 2 invents a hierarchy the copy does
   * not have. A services grid or a team, whose cards carry a paragraph and a
   * photograph, has nothing to gain from a sixth column and much to lose.
   *
   * The renderer tightens the cards past four columns and only goes wide on
   * large screens; below that they still wrap, because five cards abreast on a
   * tablet is not a row, it is five slivers.
   */
  columns: z.union([z.literal(2), z.literal(3), z.literal(4), z.literal(5), z.literal(6)]).default(3),
  /**
   * The first three are card treatments and differ only in how the surface is
   * drawn. `connected` is a different object altogether: no cards at all, but a
   * single accent rule running the width of the row with a node under each
   * item, and a hairline dropping between them.
   *
   * It exists because a set of pillars is not a set of cards. Cards say "here
   * are six things, consider each"; a connected row says "here are six things,
   * and they are one idea" — which is exactly the claim a set of values or an
   * acronym makes. The renderer also lifts the first letter of each title into
   * the accent colour, so a row whose initials spell something shows it.
   */
  style: z.enum(["bordered", "elevated", "plain", "connected"]).default("bordered"),
  /**
   * Whether each card's description is always visible, or waits for the
   * pointer.
   *
   * `hover` suits a row of one-word values, where the titles are the content
   * and the descriptions are the footnote. It is a presentation choice and not
   * a way to hide anything: the text stays in the document at full strength,
   * so a screen reader reads it whether or not it is on screen, and a touch
   * device — which has no hover to give — shows it outright rather than
   * leaving the reader with five bare words.
   */
  descriptionReveal: z.enum(["always", "hover"]).default("always")
});
var statisticSchema = z.object({
  value: z.string().min(1, "A figure is required").max(20),
  label: z.string().min(1, "A label is required").max(80),
  description: z.string().max(200).default(""),
  prefix: z.string().max(8).default(""),
  suffix: z.string().max(8).default("")
});
var statisticsContentSchema = z.object({
  eyebrow: eyebrowSchema,
  heading: headingSchema,
  stats: z.array(statisticSchema).max(6).default([]),
  /**
   * A required qualifier. Figures on a financial advisory site read as
   * performance claims whether or not they are meant to; the schema makes the
   * caveat structural rather than optional.
   */
  disclaimer: z.string().max(300).default("")
});
var processStepSchema = z.object({
  title: z.string().min(1, "Step title is required").max(120),
  description: z.string().max(400).default(""),
  icon: z.string().max(60).default(""),
  duration: z.string().max(40).default("")
});
var processStepsContentSchema = z.object({
  eyebrow: eyebrowSchema,
  heading: headingSchema,
  intro: subheadingSchema,
  /** When set, steps are pulled from that service instead of `steps`. */
  serviceId: z.string().optional(),
  steps: z.array(processStepSchema).max(8).default([]),
  layout: z.enum(["horizontal", "vertical", "numbered"]).default("horizontal")
});
var faqAccordionContentSchema = z.object({
  eyebrow: eyebrowSchema,
  heading: headingSchema.default("Frequently asked questions"),
  intro: subheadingSchema,
  /** Empty means all published FAQs, optionally narrowed by service. */
  faqIds: z.array(z.string()).max(30).default([]),
  serviceId: z.string().optional(),
  categoryLabel: z.string().max(80).default(""),
  /** Emits FAQPage structured data. Only valid once per page. */
  emitStructuredData: z.boolean().default(true),
  allowMultipleOpen: z.boolean().default(false)
});
var testimonialsContentSchema = z.object({
  eyebrow: eyebrowSchema,
  heading: headingSchema,
  testimonialIds: z.array(z.string()).max(12).default([]),
  serviceId: z.string().optional(),
  featuredOnly: z.boolean().default(false),
  layout: z.enum(["carousel", "grid", "single"]).default("grid"),
  columns: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(3),
  showRating: z.boolean().default(false)
});
var teamContentSchema = z.object({
  eyebrow: eyebrowSchema,
  heading: headingSchema.default("Our team"),
  intro: subheadingSchema,
  teamMemberIds: z.array(z.string()).max(24).default([]),
  columns: z.union([z.literal(2), z.literal(3), z.literal(4)]).default(3),
  showBio: z.boolean().default(true),
  showCredentials: z.boolean().default(true),
  showContact: z.boolean().default(false)
});
var articleListingContentSchema = z.object({
  eyebrow: eyebrowSchema,
  heading: headingSchema.default("Latest insights"),
  intro: subheadingSchema,
  source: z.enum(["latest", "featured", "category", "manual"]).default("latest"),
  categoryId: z.string().optional(),
  postIds: z.array(z.string()).max(12).default([]),
  limit: z.number().int().min(1).max(12).default(3),
  columns: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(3),
  showExcerpt: z.boolean().default(true),
  showAuthor: z.boolean().default(true),
  showDate: z.boolean().default(true),
  showReadingTime: z.boolean().default(true),
  viewAllButton: buttonSchema.partial().optional()
});
var consultationCtaContentSchema = z.object({
  eyebrow: eyebrowSchema,
  heading: headingSchema.default("Ready to talk?"),
  body: subheadingSchema,
  primaryButton: buttonSchema.default({
    label: "Book a consultation",
    href: "/book-consultation",
    variant: "primary",
    opensInNewTab: false
  }),
  secondaryButton: buttonSchema.partial().optional(),
  image: imageRefSchema.optional(),
  layout: z.enum(["banner", "card", "split"]).default("banner")
});
var contactFormContentSchema = z.object({
  eyebrow: eyebrowSchema,
  heading: headingSchema.default("Get in touch"),
  intro: subheadingSchema,
  formType: z.enum(["contact", "consultation", "newsletter"]).default("contact"),
  showContactDetails: z.boolean().default(true),
  showOfficeHours: z.boolean().default(true),
  successMessage: z.string().max(300).default("Thank you. We will be in touch within one business day."),
  /** Consent wording shown beside the required data-processing checkbox. */
  consentText: z.string().max(500).default(
    "I consent to VEO Consultancy storing and processing my details in order to respond to this enquiry."
  )
});
var imageGalleryContentSchema = z.object({
  eyebrow: eyebrowSchema,
  heading: headingSchema,
  images: z.array(imageRefSchema).max(24).default([]),
  layout: z.enum(["grid", "masonry", "carousel"]).default("grid"),
  columns: z.union([z.literal(2), z.literal(3), z.literal(4)]).default(3),
  showCaptions: z.boolean().default(false),
  enableLightbox: z.boolean().default(true)
});
var riskDisclaimerContentSchema = z.object({
  heading: z.string().max(200).default("Important information"),
  body: richTextSchema.default(emptyRichText),
  variant: z.enum(["subtle", "bordered", "prominent"]).default("bordered"),
  icon: z.boolean().default(true)
});
var mottoItemSchema = z.object({
  label: z.string().min(1, "Label is required").max(40),
  /** Lucide icon name. */
  icon: z.string().max(60).default("")
});
var mottoContentSchema = z.object({
  eyebrow: eyebrowSchema,
  heading: headingSchema,
  /** The motto itself. Set large and uppercase, so it has to stay short. */
  motto: z.string().max(120).default(""),
  /** An optional quieter line beneath it — a source, a reference, a gloss. */
  supporting: z.string().max(160).default(""),
  /**
   * The words the motto rests on, set as a row inside the band.
   *
   * Capped at six because they are set on one line on a desktop viewport and a
   * seventh either wraps into a stray orphan or shrinks the whole row below
   * the size at which a label read at a glance is legible.
   */
  items: z.array(mottoItemSchema).max(6).default([]),
  /** Lucide icon name shown in the band, unless an emblem is set. */
  icon: z.string().max(60).default("Quote"),
  /** An image used in place of the icon: a QR code, a crest, a wordmark. */
  emblem: imageRefSchema.optional(),
  layout: z.enum(["band", "plain"]).default("band")
});
var BlockContentRegistry = {
  hero: heroContentSchema,
  heroSlider: heroSliderContentSchema,
  richText: richTextContentSchema,
  textImage: textImageContentSchema,
  servicesGrid: servicesGridContentSchema,
  featureCards: featureCardsContentSchema,
  statistics: statisticsContentSchema,
  processSteps: processStepsContentSchema,
  faqAccordion: faqAccordionContentSchema,
  testimonials: testimonialsContentSchema,
  team: teamContentSchema,
  articleListing: articleListingContentSchema,
  consultationCta: consultationCtaContentSchema,
  contactForm: contactFormContentSchema,
  imageGallery: imageGalleryContentSchema,
  riskDisclaimer: riskDisclaimerContentSchema,
  motto: mottoContentSchema
};
function parseSectionContent(sectionType, content) {
  return BlockContentRegistry[sectionType].parse(content);
}
function defaultSectionContent(sectionType) {
  return BlockContentRegistry[sectionType].parse({});
}
function isSectionType(value) {
  return typeof value === "string" && value in BlockContentRegistry;
}
var SECTION_DEFINITIONS = {
  hero: {
    type: "hero",
    label: "Hero",
    description: "Full-width opening section with a headline and call to action.",
    icon: "LayoutPanelTop",
    group: "layout",
    isDynamic: false
  },
  heroSlider: {
    type: "heroSlider",
    label: "Hero slider",
    description: "A rotating set of full-width opening slides, each with its own image and copy.",
    icon: "GalleryHorizontal",
    group: "layout",
    isDynamic: false
  },
  richText: {
    type: "richText",
    label: "Rich text",
    description: "Formatted prose with headings, lists and links.",
    icon: "Type",
    group: "content",
    isDynamic: false
  },
  textImage: {
    type: "textImage",
    label: "Text and image",
    description: "Copy beside an image, in either order.",
    icon: "Columns2",
    group: "content",
    isDynamic: false
  },
  servicesGrid: {
    type: "servicesGrid",
    label: "Services grid",
    description: "Cards for your advisory services, pulled from the services library.",
    icon: "Grid3x3",
    group: "marketing",
    isDynamic: true
  },
  featureCards: {
    type: "featureCards",
    label: "Feature cards",
    description: "A set of cards for benefits, differentiators or highlights.",
    icon: "LayoutGrid",
    group: "marketing",
    isDynamic: false
  },
  statistics: {
    type: "statistics",
    label: "Statistics",
    description: "Headline figures with a required qualifying note.",
    icon: "ChartNoAxesColumn",
    group: "marketing",
    isDynamic: false
  },
  processSteps: {
    type: "processSteps",
    label: "Process steps",
    description: "How working with you unfolds, step by step.",
    icon: "ListOrdered",
    group: "marketing",
    isDynamic: true
  },
  faqAccordion: {
    type: "faqAccordion",
    label: "FAQ accordion",
    description: "Expandable questions and answers, with structured data.",
    icon: "MessageCircleQuestion",
    group: "content",
    isDynamic: true
  },
  testimonials: {
    type: "testimonials",
    label: "Testimonials",
    description: "Client quotes. Only testimonials with recorded consent appear.",
    icon: "Quote",
    group: "social-proof",
    isDynamic: true
  },
  team: {
    type: "team",
    label: "Team",
    description: "Advisor profiles with credentials.",
    icon: "Users",
    group: "social-proof",
    isDynamic: true
  },
  articleListing: {
    type: "articleListing",
    label: "Article listing",
    description: "Insight articles, latest or hand-picked.",
    icon: "Newspaper",
    group: "content",
    isDynamic: true
  },
  consultationCta: {
    type: "consultationCta",
    label: "Consultation CTA",
    description: "Prompt to book a consultation. The site's main conversion point.",
    icon: "CalendarCheck",
    group: "conversion",
    isDynamic: false
  },
  contactForm: {
    type: "contactForm",
    label: "Contact form",
    description: "Enquiry form with consent capture and office details.",
    icon: "Mail",
    group: "conversion",
    isDynamic: false
  },
  imageGallery: {
    type: "imageGallery",
    label: "Image gallery",
    description: "A set of images in a grid, masonry or carousel.",
    icon: "Images",
    group: "content",
    isDynamic: false
  },
  riskDisclaimer: {
    type: "riskDisclaimer",
    label: "Risk disclaimer",
    description: "Regulatory wording, styled to be noticed rather than buried.",
    icon: "TriangleAlert",
    group: "content",
    isDynamic: false
  },
  motto: {
    type: "motto",
    label: "Motto",
    description: "One line the organisation stands by, set in a band of its own.",
    icon: "Megaphone",
    group: "content",
    isDynamic: false
  }
};
var SECTION_GROUPS = [
  { key: "layout", label: "Layout" },
  { key: "content", label: "Content" },
  { key: "marketing", label: "Marketing" },
  { key: "social-proof", label: "Social proof" },
  { key: "conversion", label: "Conversion" }
];

export { ACCEPTED_DOCUMENT_MIME_TYPES, ACCEPTED_IMAGE_MIME_TYPES, ALIGNMENTS, AUDIT_ACTIONS, AUDIT_ACTION_LABELS, BACKGROUNDS, BULK_USER_ACTIONS, BlockContentRegistry, CONSULTATION_MODES, CONSULTATION_MODE_LABELS, CONTAINER_WIDTHS, CONTENT_ANIMATIONS, CORE_SERVICE_SLUGS, ENUM_TO_SECTION_TYPE, IMAGE_VARIANT_WIDTHS, LEAD_STATUSES, LEAD_STATUS_LABELS, MEDIA_TYPES, MENU_LOCATIONS, MENU_LOCATION_LABELS, MESSAGE_STATUSES, MESSAGE_STATUS_LABELS, NAV_LINK_TYPES, NAV_LINK_TYPE_LABELS, PAGE_STATUSES, PAGE_TEMPLATES, PERMISSION_DESCRIPTIONS, PERMISSION_KEYS, POST_STATUSES, PUBLISH_STATUSES, ROLE_DEFINITIONS, ROLE_KEYS, ROLE_PERMISSIONS, SECTION_DEFINITIONS, SECTION_GROUPS, SECTION_TYPES, SECTION_TYPE_TO_ENUM, SERVICE_INTERESTS, SERVICE_INTEREST_LABELS, SERVICE_STATUSES, SETTING_GROUPS, SETTING_GROUP_LABELS, SETTING_TYPES, SITE_NAME, SITE_TAGLINE, SPACINGS, SYSTEM_PAGE_PATHS, THUMBNAIL_WIDTH, TIME_SLOT_LABELS, acceptInviteSchema, activeSessionSchema, addInternalNoteSchema, alignmentSchema, articleListingContentSchema, assignLeadSchema, assignRolesSchema, auditLogEntrySchema, auditLogQuerySchema, auditLogResponseSchema, authUserSchema, backgroundSchema, benefitSchema, buildPagePath, bulkUserActionSchema, bulkUserResultSchema, buttonSchema, categorySummarySchema, changePasswordSchema, consultationCtaContentSchema, consultationDetailSchema, consultationListQuerySchema, consultationListResponseSchema, consultationSummarySchema, contactFormContentSchema, contactMessageListQuerySchema, contactMessageSchema, containerWidthSchema, contentAnimationSchema, createConsultationSchema, createContactMessageSchema, createFolderSchema, createNavigationItemSchema, createPageSchema, createPostSchema, createSectionSchema, createServiceSchema, createUserSchema, defaultSectionContent, defaultSectionSettings, defaultServiceCta, duplicatePageSchema, emailSchema, emptyRichText, emptyServiceRichText, faqAccordionContentSchema, faqSchema, featureCardSchema, featureCardsContentSchema, folderSchema, forgotPasswordSchema, formatFileSize, hasAllPermissions, hasAnyPermission, hasPermission, heroContentSchema, heroSlideSchema, heroSliderContentSchema, heroSliderSummarySchema, imageGalleryContentSchema, imageRefSchema, internalNoteSchema, isSectionType, isSlideLive, leadSubmissionResponseSchema, linkFaqsSchema, loginAttemptSchema, loginResponseSchema, loginSchema, mediaItemSchema, mediaListQuerySchema, mediaListResponseSchema, mediaUsageEntrySchema, mediaUsageSchema, mediaVariantSchema, mottoContentSchema, mottoItemSchema, navigationItemSchema, navigationMenuSchema, navigationTargetsSchema, pageDetailSchema, pageListQuerySchema, pageListResponseSchema, pageSummarySchema, pageVersionSummarySchema, paginationMetaSchema, parsePermissionKey, parseSectionContent, passwordSchema, postDetailSchema, postEditorOptionsSchema, postListQuerySchema, postListResponseSchema, postSummarySchema, processStepInputSchema, processStepSchema, processStepsContentSchema, publicServiceSchema, recordConsentSchema, redirectSchema, reorderNavigationSchema, reorderSchema, reorderSectionsSchema, reorderServicesSchema, replaceBenefitsSchema, replaceProcessStepsSchema, resetPasswordSchema, resolvePermissions, richTextContentSchema, richTextSchema, riskDisclaimerContentSchema, safeFilename, savePageVersionSchema, schedulePageSchema, schedulePostSchema, sectionResponseSchema, sectionSettingsSchema, seoMetadataSchema, serviceAudienceItemSchema, serviceAudienceSchema, serviceCtaSchema, serviceDetailSchema, serviceInclusionSchema, serviceInclusionsSchema, serviceListQuerySchema, serviceSummarySchema, servicesGridContentSchema, sessionSchema, settingSchema, slugSchema, slugify, spacingSchema, spamGuardSchema, statisticSchema, statisticsContentSchema, tagSummarySchema, teamContentSchema, teamMemberSchema, testimonialSchema, testimonialsContentSchema, textImageContentSchema, toggleSectionVisibilitySchema, updateLeadStatusSchema, updateMediaSchema, updateMessageStatusSchema, updateNavigationItemSchema, updatePageSchema, updatePostSchema, updateProfileSchema, updateRedirectSchema, updateSectionSchema, updateServiceSchema, updateSettingsSchema, updateUserSchema, upsertCategorySchema, upsertFaqSchema, upsertRedirectSchema, upsertTagSchema, upsertTeamMemberSchema, upsertTestimonialSchema, userDetailSchema, userListQuerySchema, userListResponseSchema, userSummarySchema };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map